<template>
  <b-modal
    size="lg"
    hide-footer
    no-close-on-backdrop
    v-model="show"
    class="modal"
  >
    <Preloader v-if="getIsModalLoading" />
    <template v-slot:modal-header="{ cancel }">
      <h5 class="modal-title">{{title}}</h5>
      <button
        type="button"
        aria-label="Close"
        class="close"
        @click.prevent="cancel"
      >
        <span class="material-icons">
          close
        </span>
      </button>
    </template>
    <FormGenerator
      ref="form"
      :data="form.fields"
      :textButtonSubmit="submitButtonText"
      :showReset="false"
      @submit="submit"
      @updateItem="updateFormField"
      @input="changeFormFields($event)"
    >
      <template v-slot:Contractor="slotProps">
        <b-form-group>
          <label class="form__label">{{slotProps.data.label}}</label>
          <multiselect
            ref="contractor"
            v-model="slotProps.data.value"
            :options="getContractors"
            :placeholder="slotProps.data.placeholder"
            :track-by="slotProps.data.valueField"
            :label="slotProps.data.selectLabel"
            :disabled="mode === 'edit' || !!slotProps.fields.partner.value"
            :allow-empty="false"
            :showLabels="false"
            @select="(selectedOption) => selectPayee('contractor', selectedOption)"
          >
            <template slot="clear">
              <span class="material-icons clear-select" v-show="slotProps.fields.contractor.value" @click="clearSelect('contractor')">
                close
              </span>
            </template>
          </multiselect>
        </b-form-group>
      </template>
      <template v-slot:Partner="slotProps">
        <b-form-group>
          <label class="form__label">{{slotProps.data.label}}</label>
          <multiselect
            ref="partner"
            v-model="slotProps.data.value"
            :options="getAllPartners"
            :placeholder="slotProps.data.placeholder"
            :track-by="slotProps.data.valueField"
            :label="slotProps.data.selectLabel"
            :disabled="mode === 'edit' || !!slotProps.fields.contractor.value"
            :allow-empty="false"
            :showLabels="false"
            @select="(selectedOption) => selectPayee('partner', selectedOption)"
          >
            <template slot="clear">
              <span class="material-icons clear-select" v-show="slotProps.fields.partner.value" @click="clearSelect('partner')">
                close
              </span>
            </template>
          </multiselect>
        </b-form-group>
      </template>
      <template v-slot:ContractorBankDetails="slotProps">
        <b-form-group>
          <label class="form__label">{{slotProps.data.label}}
            <span @click="fetchBankDetails()" class="ml-1" title="Refresh">
              <font-awesome-icon icon="sync-alt" role="button"/>
              <span v-if="isFetchingBankDetails"
                    class="small-spinner spinner-border text-secondary fetch-bank-details-spinner"
                    role="status">
                <span class="sr-only">Loading...</span>
              </span>
            </span>
          </label>

          <multiselect
            ref="contractor_bank_details"
            :value="slotProps.data.value"
            @input="changePayeeBankDetails('contractor_bank_details', $event)"
            :options="getContractorBankDetailsAvailable"
            :placeholder="slotProps.data.placeholder"
            :track-by="slotProps.data.valueField"
            :label="slotProps.data.selectLabel"
            :disabled="!getContractorBankDetailsAvailable.length || isCalculatingPayout"
            :allow-empty="false"
            :showLabels="false"
          />
        </b-form-group>
      </template>
      <template v-slot:PartnerBankDetails="slotProps">
        <b-form-group>
          <label class="form__label">{{slotProps.data.label}}
            <span @click="fetchBankDetails()" class="ml-1" title="Refresh"><font-awesome-icon icon="sync-alt" role="button"/></span>
            <span v-if="isFetchingBankDetails"
                  class="small-spinner spinner-border text-secondary fetch-bank-details-spinner"
                  role="status">
              <span class="sr-only">Loading...</span>
            </span>
          </label>
          <multiselect
            ref="partner_bank_details"
            :value="slotProps.data.value"
            @input="changePayeeBankDetails('partner_bank_details', $event)"
            :options="getPartnerBankDetailsAvailable"
            :placeholder="slotProps.data.placeholder"
            :track-by="slotProps.data.valueField"
            :label="slotProps.data.selectLabel"
            :disabled="!getPartnerBankDetailsAvailable.length || isCalculatingPayout"
            :allow-empty="false"
            :showLabels="false"
          />
        </b-form-group>
      </template>
      <template v-slot:CustomFile="slotProps">
        <label class="form__label">{{ slotProps.data.label }}</label>
        <div class="row">
          <div class="col">
            <b-form-file
              ref="customFile"
              v-model="slotProps.data.value"
              :placeholder="data && data.custom_file_name ? data.custom_file_name : 'Choose a custom file'"
              drop-placeholder="Drop file here..."
              class="invoice-file"
            ></b-form-file>
          </div>
          <div class="col col-md-1 my-auto" v-if="data && data.custom_file_name">
            <a
              target="_blank"
              :href="data.payment_doc_url"
              :disabled="!data.payment_doc_url"
              :title="data.custom_file_name"
            >
              <font-awesome-icon icon="file-invoice" size="2x"/>
            </a>
          </div>
        </div>
      </template>
      <template v-slot:AutoFill="slotProps">
        <b-form-group>
          <label class="form__label">{{slotProps.data.label}}</label>
          <FinancePayoutsModalAutoFill
            ref="autoFill"
            :dates="slotProps.data.value"
            :payout_id="slotProps.fields.id.value"
            :contractor="getCurrentContractor"
            :partner="getCurrentPartner"
            :currency_code="currentCurrencyCode"
            :currency_rate="slotProps.fields.currency_rate.value"
            @auto-fill="autoFillItems"
            @changeInterval="autoFillIntervalChanged"
          />
        </b-form-group>
      </template>
      <template v-slot:Items="slotProps">
        <FinancesPayoutsCompanyItemsTable
          v-if="slotProps.fields.payout_type.value && slotProps.fields.payout_type.value.id === config.payoutsTypes.by_time.id"
          v-model="slotProps.fields.items.value"
          :current-contractor="getCurrentContractor"
          :current-partner="getCurrentPartner"
          :current-choice="getChoice"
          @calculateHours="calculateHours($event)"
          @calculatePayout="calculatePayout()"
        />
      </template>
      <template v-slot:kpi_bonuses="slotProps">
        <template v-for="(kpiBonus, i) in slotProps.data.value">
          <button
            v-if="kpiBonus"
            :key="i"
            type="button"
            class="btn btn-info mr-3"
            :disabled="isCalculatingPayout"
            @click="addKpiBonus(kpiBonus)">Add KPI bonus ({{ kpiBonus }} {{ currentCurrencyCode }})</button>
        </template>
      </template>
      <template v-slot:ExtraPayments="slotProps">
        <h6 class="mt-5">{{ slotProps.data.label }}</h6>
        <FinancesPayoutsExtraPaymentsTable
          v-model="slotProps.data.value"
          :currencyCode="currentCurrencyCode"
          @dataTouched="calculatePayout"
        />
      </template>
      <template v-slot:Taxes="slotProps">
        <div v-if="slotProps.data.value.length">
          <h6 class="mt-5">{{ slotProps.data.label }}</h6>
          <FinancesPayoutsTaxesTable
            v-model="slotProps.data.value"
            :currencyCode="currentCurrencyCode"
            @calculatePayout="calculatePayout"
          />
          <Preloader v-if="isCalculatingPayout" />
        </div>
      </template>
      <template v-slot:Indicators="slotProps">
        <div v-if="Object.values(slotProps.data.value).length">
          <div v-for="(items, indicator) in slotProps.data.value" :key="indicator">{{ indicator }}
            <template v-if="indicator === 'OT'" innerText="(Over time: logged hours > working hours)" />
            <template v-else-if="indicator === 'UT'" innerText="(Under time: logged hours < working hours)" />
            <ul>
              <li v-for="(hours, name) in items" :key="name">{{ name }} (logged: {{ hours.logged_hours }}, working: {{ hours.working_hours }})</li>
            </ul>
          </div>
        </div>
      </template>
      <template v-slot:items_description="slotProps">
        <b-form-textarea
          id="textarea"
          v-model="slotProps.fields.items_description.value"
          rows="3"
          max-rows="6"
          readonly
        >
        </b-form-textarea>
        <Preloader v-if="isCalculatingPayout" />
      </template>
      <template v-slot:amount="slotProps">
        <div
          v-if="!isCalculatingPayout"
          class="payout-amount"
        >{{ slotProps.data.value }} {{ currentCurrencyCode }}</div>
        <Preloader v-else />
      </template>
      <template v-slot:currency_rate="slotProps">
        <b-form-group>
          <span>{{ slotProps.data.label }}</span>
          <b-form-input
            v-model="slotProps.data.value"
            :placeholder="slotProps.data.placeholder"
            :disabled="isCalculatingPayout"
            :readonly="defaultCompensationCurrencyCode === currentCurrencyCode"
            class="currency-rate-input"
            maxlength="7"
            type="number"
            step=".01"
            debounce="1000"
            @input="calculatePayout()"
          />
          <span style="margin-left: 10px;">{{ currentCurrencyCode ? `${defaultCompensationCurrencyCode} / ${currentCurrencyCode}` : ''}}</span>
          <slot name="errors"></slot>
        </b-form-group>
      </template>
    </FormGenerator>
  </b-modal>
</template>

<script>
import Moment from 'moment'
import { cloneDeep, debounce } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import FormGenerator from '@components/FormGenerator'
import Preloader from '@components/Preloader'
import FinancesPayoutsCompanyItemsTable from '@components/modals/FinancePayoutsModal/FinancesPayoutsCompanyItemsTable'
import FinancePayoutsModalAutoFill from '@components/modals/FinancePayoutsModal/FinancePayoutsModalAutoFill'
import FinancesPayoutsExtraPaymentsTable from '@components/modals/FinancePayoutsModal/FinancesPayoutsExtraPaymentsTable'
import FinancesPayoutsTaxesTable from '@components/modals/FinancePayoutsModal/FinancesPayoutsTaxesTable'

export default {
  components: {
    Preloader,
    FormGenerator,
    FinancesPayoutsCompanyItemsTable,
    FinancesPayoutsExtraPaymentsTable,
    FinancesPayoutsTaxesTable,
    FinancePayoutsModalAutoFill
  },
  props: {
    mode: {
      type: String,
      default: 'create'
    },
    title: {
      type: String
    },
    submitButtonText: {
      type: String
    },
    value: {
      type: Boolean
    },
    data: {
      type: Object
    }
  },
  data () {
    const that = this
    const defaultForm = {
      fields: {
        'id': {
          name: 'id',
          hidden: true
        },
        'contractor': {
          type: 'slot',
          slotName: 'Contractor',
          name: 'contractor_id',
          label: 'Contractors',
          selectLabel: 'label',
          valueField: 'id',
          options: [],
          value: null,
          class: 'col-6',
          validation: [
            {
              name: 'requiredIf',
              params: () => !that.form.fields.partner.value
            }
          ]
        },
        'partner': {
          type: 'slot',
          slotName: 'Partner',
          name: 'partner_id',
          label: 'Partners',
          selectLabel: 'company_name',
          valueField: 'id',
          options: [],
          value: null,
          class: 'col-6'
        },
        'contractor_bank_details': {
          type: 'slot',
          slotName: 'ContractorBankDetails',
          name: 'contractor_bank_details_id',
          label: 'Contractor Bank Details',
          selectLabel: 'title',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            {
              name: 'requiredIf',
              params: () => !!that.form.fields.contractor.value
            }
          ],
          class: 'col-6'
        },
        'partner_bank_details': {
          type: 'slot',
          slotName: 'PartnerBankDetails',
          name: 'partner_bank_details_id',
          label: 'Partner Bank Details',
          selectLabel: 'title',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            {
              name: 'requiredIf',
              params: () => !!that.form.fields.partner.value
            }
          ],
          class: 'col-6'
        },
        'own_bank_details': {
          type: 'select',
          name: 'own_bank_details_id',
          label: 'Own Bank Details',
          selectLabel: 'summary',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            { name: 'required' }
          ],
          class: 'col-12'
        },
        'payout_status': {
          type: 'select',
          name: 'payout_status_id',
          label: 'Payout Status',
          selectLabel: 'display_name',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            { name: 'required' }
          ],
          class: 'col-12 col-md-4'
        },
        'payout_template_type': {
          type: 'select',
          name: 'payout_template_type_id',
          label: 'Payout Template Type',
          selectLabel: 'display_name',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            { name: 'required' }
          ],
          class: 'col-12 col-md-4'
        },
        'custom_file': {
          type: 'slot',
          slotName: 'CustomFile',
          name: 'custom_file',
          label: 'Custom doc file',
          value: null,
          class: 'col-12 col-md-4',
          hidden: true
        },
        'comment': {
          type: 'input',
          name: 'comment',
          label: 'Comment',
          placeholder: 'Comment',
          validation: [
            { name: 'required' }
          ],
          defaultValue: 'Software development and consulting for %dateFrom - %dateTo',
          edited: false,
          value: null,
          class: 'col-12'
        },
        'comment_national': {
          type: 'input',
          name: 'comment_national',
          label: 'Comment National',
          placeholder: 'Comment National',
          validation: [
            { name: 'required' }
          ],
          defaultValue: 'Software development and consulting for %dateFrom - %dateTo',
          edited: false,
          value: null,
          class: 'col-12'
        },
        'issue_date': {
          type: 'datepicker',
          name: 'issue_date',
          label: 'Issue Date',
          format: 'yyyy-MM-dd',
          placeholder: 'Issue Date',
          class: 'col-12 col-md-4',
          value: Moment().format('YYYY-MM-DD')
        },
        'due_date': {
          type: 'datepicker',
          name: 'due_date',
          label: 'Due Date',
          format: 'yyyy-MM-dd',
          placeholder: 'Due Date',
          class: 'col-12 col-md-4',
          value: null
        },
        'paid_date': {
          type: 'datepicker',
          name: 'paid_date',
          label: 'Paid Date',
          format: 'yyyy-MM-dd',
          placeholder: 'Paid Date',
          class: 'col-12 col-md-4',
          defaultValue: Moment().format('YYYY-MM-DD'),
          value: null,
          hidden: false
        },
        'payout_type': {
          type: 'select',
          name: 'payout_type_id',
          label: 'Payout Type',
          selectLabel: 'type',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            { name: 'required' }
          ],
          class: 'col-12'
        },
        'auto_fill': {
          type: 'slot',
          slotName: 'AutoFill',
          name: 'auto_fill',
          label: 'Auto Fill Contractors (all items will be replaced)',
          value: {
            date_from: this._toLocalTime(Moment().subtract(1, 'months').startOf('month')),
            date_to: this._toLocalTime(Moment().subtract(1, 'months').endOf('month'))
          },
          class: 'col-12'
        },
        'items': {
          type: 'slot',
          slotName: 'Items',
          name: 'items',
          label: 'Payout Items',
          value: null,
          class: 'col-12'
        },
        'kpi_bonuses': {
          type: 'slot',
          slotName: 'kpi_bonuses',
          name: 'kpi_bonuses',
          label: 'KPI',
          value: null,
          class: 'col-12'
        },
        'extra_payments': {
          type: 'slot',
          slotName: 'ExtraPayments',
          name: 'extra_payments',
          label: 'Extra Payments',
          value: null,
          class: 'col-12',
          validation: [
            {
              name: 'requiredIf',
              params: () => that.form.fields.payout_type.value === that.types.find(el => el.type === 'Only extra payments')
            }
          ]
        },
        'taxes': {
          type: 'slot',
          slotName: 'Taxes',
          name: 'taxes',
          label: 'Taxes',
          value: [],
          class: 'col-12'
        },
        'indicators': {
          type: 'slot',
          slotName: 'Indicators',
          value: {},
          class: 'col-12'
        },
        'items_description': {
          type: 'slot',
          slotName: 'items_description',
          name: 'items_description',
          label: 'Items Description',
          placeholder: 'Auto - generated description',
          value: null,
          class: 'col-12',
          validation: [
            { name: 'required' }
          ]
        },
        'currency_rate': {
          type: 'slot',
          slotName: 'currency_rate',
          name: 'currency_rate',
          label: 'Currency rate',
          placeholder: 'Currency rate',
          value: '',
          class: 'col-6'
        },
        'amount': {
          type: 'slot',
          slotName: 'amount',
          name: 'amount',
          label: 'Amount',
          value: null,
          class: 'col-6'
        }
      }
    }

    return {
      defaultForm,
      form: cloneDeep(defaultForm),
      currentPayoutType: 1,
      config: {
        payoutsTypes: {
          by_time: { id: 1 },
          only_extra_payments: { id: 2 }
        }
      },
      types: [
        { id: 1, type: 'By time' },
        { id: 2, type: 'Only extra payments' }
      ],
      defaultCompensationCurrencyCode: 'USD',
      isCalculatingPayout: false,
      isFetchingBankDetails: false
    }
  },
  computed: {
    ...mapGetters({
      getIsModalLoading: 'finances/getIsModalLoading',
      getAllUsers: 'getAllUsers',
      getAllPartners: 'partners/getAllPartners',
      getPayoutsStatuses: 'finances/getPayoutsStatuses',
      getPayoutsTemplateTypes: 'finances/getPayoutsTemplateTypes',
      getContractorBankDetails: 'finances/getContractorBankDetails',
      getPartnerBankDetails: 'finances/getPartnerBankDetails',
      getOwnBankDetails: 'finances/getOwnBankDetails',
      getPayoutsTypes: 'finances/getPayoutsTypes',
      getProjects: 'projects/getProjects'
    }),
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    getContractors () {
      return this._getPartnerRelatedUsers(null) || []
    },
    getCurrentContractor () {
      return this.form.fields.contractor.value
    },
    getCurrentPartner () {
      return this.form.fields.partner.value
    },
    currentCurrencyCode () {
      if (this.form.fields.partner_bank_details.value) {
        return this.form.fields.partner_bank_details.value.currency.code
      }
      if (this.form.fields.contractor_bank_details.value) {
        return this.form.fields.contractor_bank_details.value.currency.code
      }
      return ''
    },
    getChoice () {
      if (this.form.fields.contractor.value) {
        return 'Contractor'
      } else if (this.form.fields.partner.value) {
        return 'Partner'
      }
      return ''
    },
    getContractorBankDetailsAvailable () {
      if (!this.getCurrentContractor) return []
      const currentContractorId = this.getCurrentContractor.id
      return this.getContractorBankDetails.filter(item => {
        return item.contractor_id === currentContractorId
      })
    },
    getPartnerBankDetailsAvailable () {
      if (!this.getCurrentPartner) return []
      const currentPartnerId = this.getCurrentPartner.id
      return this.getPartnerBankDetails.filter(item => {
        return item.partner_id === currentPartnerId
      })
    }
  },
  watch: {
    'value': {
      deep: true,
      handler () {
        if (this.value) {
          if (!this.data) {
            this.form = cloneDeep(this.defaultForm)
          }
          this.setInitialForm()
        }
      }
    },
    'data': {
      deep: true,
      handler (value) {
        this.setParamsForEdit(value)
        this.calculatePayout()
      }
    },
    'form.fields.payout_type.value': {
      deep: true,
      handler (value) {
        if (value.id === this.config.payoutsTypes.only_extra_payments.id) {
          this.calculatePayout()
          this.form.fields.items.value = null
        }
      }
    },
    'form.fields.payout_status': {
      deep: true,
      handler () {
        this.form.fields.paid_date.hidden = this.form.fields.payout_status.value && this.form.fields.payout_status.value.name !== 'paid'
        if (this.form.fields.payout_status.value && this.form.fields.payout_status.value.name === 'paid') {
          this.form.fields.paid_date.value = this.form.fields.paid_date.value ? this.form.fields.paid_date.value : this.form.fields.paid_date.defaultValue
        } else {
          this.form.fields.paid_date.value = null
        }
      }
    },
    'form.fields.payout_template_type': {
      deep: true,
      handler (payoutTemplateType) {
        this.form.fields.custom_file.hidden = !(payoutTemplateType.value && payoutTemplateType.value.name === 'custom')
      }
    }
  },
  mounted () {
    this.setInitialForm()
  },
  methods: {
    ...mapActions({
      setIsModalLoading: 'finances/setIsModalLoading'
    }),
    setInitialForm () {
      this._toggleDataPageLoading(async () => {
        this.setAllOptions()
      })
    },
    setParamsForEdit (inData) {
      if (!inData) return

      let data = cloneDeep(inData)

      this.form.fields.id.value = data.id
      if (data.contractor) {
        this.form.fields.contractor.value = data.contractor
        this.form.fields.contractor_bank_details.value = data.payee_bank_details
        this.form.fields.partner.value = null
        this.form.fields.partner_bank_details.value = null
      }
      if (data.partner) {
        this.form.fields.partner.value = data.partner
        this.form.fields.partner_bank_details.value = data.payee_bank_details
        this.form.fields.contractor.value = null
        this.form.fields.contractor_bank_details.value = null
      }
      this.form.fields.own_bank_details.value = data.own_bank_details
      this.form.fields.payout_status.value = data.payout_status
      this.form.fields.payout_template_type.value = this.preparePayoutTemplateType(data.payout_template_type)

      this.form.fields.comment.value = data.comment
      this.form.fields.comment.edited = true
      this.form.fields.comment_national.value = data.comment_national
      this.form.fields.comment_national.edited = true

      this.form.fields.issue_date.value = this._toLocalTime(data.issue_date)
      this.form.fields.due_date.value = this._toLocalTime(data.due_date)
      this.form.fields.paid_date.value = this._toLocalTime(data.paid_date)

      this.form.fields.payout_type.value = data.payout_type

      if (data.auto_fill) {
        this.form.fields.auto_fill.value = data.auto_fill
      }
      this.form.fields.items.value = this.prepareItems(data.contractors)
      this.form.fields.extra_payments.value = data.extra_payments
      this.form.fields.taxes.value = data.taxes.map(function (item) {
        item.is_manually_edit = !!item.is_manually_edit
        return item
      })
      this.form.fields.currency_rate.value = String(data.currency_rate)
      this.form.fields.items_description.value = data.items_description
      this.form.fields.amount.value = data.amount

      this.form.fields.indicators.value = {}
      this.form.fields.custom_file.value = null
    },
    prepareItems: items => items.map(item => {
      return {
        ...item,
        'date_from': Moment(item.date_from).format(),
        'date_to': Moment(item.date_to).format(),
        'contractor_id': item.contractor ? item.contractor.id : null,
        'contractor': item.contractor,
        'project_key': item.project ? item.project.key : null,
        'project': item.project,
        'calculateHours': true,
        'hoursCalculating': false
      }
    }),
    setAllOptions () {
      this.setPayoutsTypes()
      this.setPayoutsStatuses()
      this.form.fields.partner_bank_details.options = this.getPartnerBankDetails
      this.form.fields.contractor_bank_details.options = this.getContractorBankDetails
      this.form.fields.own_bank_details.options = this.getOwnBankDetails

      this.form.fields.comment.value = this.generateCommentText(this.form.fields.auto_fill.value, 'comment')
      this.form.fields.comment_national.value = this.generateCommentText(this.form.fields.auto_fill.value, 'comment_national')
      this.form.fields.custom_file.value = null

      this.setTemplates()
    },
    setTemplates () {
      this.form.fields.payout_template_type.options = this.getPayoutsTemplateTypes.map(item => {
        item = this.preparePayoutTemplateType(item)
        return item
      })
      if (!this.form.fields.payout_template_type.value) {
        this.form.fields.payout_template_type.value = this.getPayoutsTemplateTypes.find(el => el.name === 'giraffeteam_for_accountant')
      }
    },
    preparePayoutTemplateType (item) {
      item.display_name = item.name.charAt(0).toUpperCase() + item.name.slice(1).replaceAll('_', ' ')
      return item
    },
    setPayoutsTypes () {
      this.form.fields.payout_type.options = this.types
      if (!this.form.fields.payout_type || !this.form.fields.payout_type.value) {
        this.form.fields.payout_type.value = this.types.find(el => el.type === 'By time')
      }
    },
    setPayoutsStatuses () {
      this.form.fields.payout_status.options = this.getPayoutsStatuses
      if (!this.form.fields.payout_status || !this.form.fields.payout_status.value) {
        this.form.fields.payout_status.value = this.getPayoutsStatuses.find(el => el.display_name === 'New')
      }
    },
    async submit (fields) {
      if (this.isCalculatingPayout || this.hasNotValidItems()) return

      const data = this.prepareDataForRequest(fields)

      if (this.$refs['customFile'] && this.$refs['customFile'].value) {
        const customFileBase64 = await this.fileToBase64(this.$refs['customFile'].value)
        data['custom_file'] = customFileBase64
      } else {
        data['custom_file'] = null
      }
      this.$emit('submit', data)
    },
    calculatePayout: debounce(function () {
      this.calculatePayoutRequest()
    }, 1500),
    async calculatePayoutRequest () {
      const data = this.getPreparedDataForRequest()

      if (data.items) {
        data.items = data.items.filter(item => item.contractor_id && item.project_key && item.date_from && item.date_to)
      }

      if (
        this.isCalculatingPayout ||
        (!data.contractor_id && !data.partner_id) ||
        (data.contractor_id && !data.contractor_bank_details_id) ||
        (data.partner_id && !data.partner_bank_details_id)
      ) return

      this.isCalculatingPayout = true

      return this.axios.post(
        `/api/finances/payouts/calculate`,
        data,
        {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        }
      ).then(({ data }) => {
        this.form.fields.amount.value = data.amount
        this.form.fields.items_description.value = data.items_description
        this.form.fields.taxes.value = data.taxes
        this.form.fields.kpi_bonuses.value = data.kpi_bonuses
        this.form.fields.indicators.value = data.indicators
        this.config.defaultCompensationCurrencyCode = data.default_compensation_currency_code

        if (data.default_compensation_currency_rate) {
          this.form.fields.currency_rate.value = data.default_compensation_currency_rate.toString()
        }
      }).finally(() => {
        this.isCalculatingPayout = false
      })
    },
    fetchPreFillPayoutData (contractorId, partnerId) {
      return this.axios.post('/api/finances/payouts/pre-fill-payout-data/',
        {
          contractor_id: contractorId,
          partner_id: partnerId
        },
        { headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') } })
    },
    changeFormFields (event) {
      this.form.fields = event
    },
    updateFormField (fieldName) {
      if (fieldName === 'comment') {
        this.form.fields.comment.edited = true
      }
      if (fieldName === 'comment_national') {
        this.form.fields.comment_national.edited = true
      }
    },
    clearSelect (fieldName) {
      this.$refs[fieldName].isOpen = false
      this.form.fields[fieldName].value = null
      this.form.fields[`${fieldName}_bank_details`].options = []
      this.form.fields[`${fieldName}_bank_details`].value = null
    },
    selectPayee (type, val) {
      this.form.fields.contractor_bank_details.value = null
      this.form.fields.partner_bank_details.value = null

      this.form.fields.items.value = []
      let contractorId = null
      let partnerId = null

      if (type === 'contractor') {
        contractorId = val.id
      } else if (type === 'partner') {
        partnerId = val.id
      }
      this.preFillPayoutData(contractorId, partnerId)
        .then(() => {
          this.$refs['autoFill'].autoFillRequest()
        })
    },
    async changePayeeBankDetails (formFieldName, $event) {
      if (this.currentCurrencyCode && this.currentCurrencyCode !== $event.currency.code) {
        const confirm = await this.$root.$confirm.open(
          'Change currency',
          'Are your sure you want to change currency from ' + this.currentCurrencyCode + ' to ' + $event.currency.code + '?' +
          '\n (currency rate will be reset)'
        )
        if (!confirm) return
        this.form.fields.currency_rate.value = null
      }
      this.form.fields[formFieldName].value = $event

      const lastOwnBankDetails = this.getOwnBankDetails.filter(item => item.currency.code === $event.currency.code).sort((a, b) => a.id > b.id).slice(0, 1).pop()
      this.form.fields['own_bank_details'].value = lastOwnBankDetails

      this.calculatePayout()
    },
    async preFillPayoutData (contractorId, partnerId) {
      this.setIsModalLoading(true)

      return this.fetchPreFillPayoutData(contractorId, partnerId)
        .then(({ data }) => {
          if (Object.keys(data).length > 0) {
            if (data.contractors.length) {
              const newDateFrom = data.contractors.reduce(
                (minDate, contractor) => minDate.isAfter(contractor.date_to, 'YYYY-MM-DD') ? Moment(contractor.date_to) : minDate,
                Moment(data.contractors[0].date_to)
              ).add(1, 'day')
              const newDateTo = cloneDeep(newDateFrom).add(1, 'months').format()

              data.auto_fill = {}
              data.auto_fill.date_from = newDateFrom.format()
              data.auto_fill.date_to = newDateTo

              data.contractors = []
            }
            this.setParamsForEdit(data)
            this.calculatePayout()

            this.form.fields.comment.edited = false
            this.form.fields.comment.value = this.generateCommentText(this.form.fields.auto_fill.value, 'comment')
            this.form.fields.comment_national.edited = false
            this.form.fields.comment_national.value = this.generateCommentText(this.form.fields.auto_fill.value, 'comment_national')
          }
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          this.setIsModalLoading(false)
        })
    },
    calculateHours: debounce(function ({ item, previousItem }) {
      if (item.contractor && item.project && item.date_from && item.date_to) {
        item.hoursCalculating = true

        this.axios.post('/api/working-time/working-hours-for-payouts',
          {
            'user_id': item.contractor.id,
            'project_key': item.project.key,
            'date_from': this._toLocalTime(item.date_from),
            'date_to': this._toLocalTime(item.date_to),
            'date_from_previous': this._toLocalTime(previousItem.date_from),
            'date_to_previous': this._toLocalTime(previousItem.date_to),
            'currency_code': this.currentCurrencyCode,
            'currency_rate': this.form.fields.currency_rate.value,
            'hours': item.hours
          },
          { headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') } }
        )
          .then(({ data }) => {
            if (item.calculateHours) {
              item.hours = data.logged_hours
              item.hour_cost = data.hour_cost
            }
            item.payment_type = data.payment_type
            item.amount = data.amount
            item.compensation_currency_code = data.compensation_currency_code
            item.logged_hours = data.logged_hours
            item.isNotValid = false

            this.calculatePayout()
          })
          .catch(({ response }) => {
            const errors = Object.values(response.data.errors)
            for (var i = 0; i < errors.length; i++) {
              this.$snotify.error(
                errors[i][0],
                'Error',
                this.$store.state.snotify
              )
            }
            item.isNotValid = true
          })
          .finally(() => {
            item.hoursCalculating = false
          })
      }
    }, 1500),
    async autoFillItems (data) {
      this.form.fields.items.value = []

      for (const i in data) {
        data[i].date_from = Moment(data[i].date_from).format()
        data[i].date_to = Moment(data[i].date_to).format()
        const item = {
          ...data[i],
          calculateHours: true,
          hoursCalculating: false
        }
        this.form.fields.items.value.push(item)
        if (item.hour_cost === 0) {
          await this.calculateHours({ item, previousItem: item })
        }
      }
      await this.calculatePayoutRequest()
    },
    getPreparedDataForRequest () {
      return this.prepareDataForRequest(this.form.fields)
    },
    prepareDataForRequest (fields) {
      let newFields = cloneDeep(fields)

      newFields.issue_date.value = this._toLocalTime(newFields.issue_date.value)
      newFields.due_date.value = this._toLocalTime(newFields.due_date.value)
      if (newFields.paid_date) {
        newFields.paid_date.value = this._toLocalTime(newFields.paid_date.value)
      }

      if (newFields.payout_type.value.id === this.config.payoutsTypes.only_extra_payments.id) {
        newFields.items.value = []
      }

      if (newFields.items.value) {
        newFields.items.value = newFields.items.value.map(item => {
          return {
            ...item,
            contractor_id: item.contractor ? item.contractor.id : null,
            project_key: item.project ? item.project.key : null,
            date_from: this._toLocalTime(item.date_from),
            date_to: this._toLocalTime(item.date_to)
          }
        })
      }
      if (newFields.taxes.value) {
        newFields.taxes.value = newFields.taxes.value.map(item => {
          return {
            'tax_type_id': item.tax_type ? item.tax_type.id : null,
            'user_id': item.user ? item.user.id : null,
            'amount': item.amount,
            'is_manually_edit': item.is_manually_edit,
            'description': item.description
          }
        })
      }
      return this.prepareFormFieldsForRequest(newFields)
    },
    prepareFormFieldsForRequest (fields) {
      let data = {}

      for (let key in fields) {
        const field = fields[key]
        const name = field['name']
        const value = field['value']
        if (!value) {
          continue
        }
        data[name] = typeof value === 'object' && !(value instanceof Array) ? value[field['valueField']] : value
      }
      return data
    },
    async fetchBankDetails () {
      this.isFetchingBankDetails = true
      await this.$api.finances.getBankDetailsAll()
      this.isFetchingBankDetails = false
    },
    validateForm () {
      this.$refs['form'].validateForm()
    },
    autoFillIntervalChanged (dates) {
      if (!this.form.fields.comment.edited) {
        this.form.fields.comment.value = this.generateCommentText(dates, 'comment')
      }
      if (!this.form.fields.comment_national.edited) {
        this.form.fields.comment_national.value = this.generateCommentText(dates, 'comment_national')
      }
    },
    generateCommentText (dates, field = 'comment') {
      const autoFillDateFrom = this._toLocalTime(dates.date_from, 'MMM DD YYYY')
      const autoFillDateTo = this._toLocalTime(dates.date_to, 'MMM DD YYYY')
      return this.form.fields[field].defaultValue.replace('%dateFrom', autoFillDateFrom).replace('%dateTo', autoFillDateTo)
    },
    addKpiBonus (bonus) {
      this.form.fields.extra_payments.value.push({
        'cost': bonus,
        'description': 'KPI bonus'
      })
      this.calculatePayout()
    },
    hasNotValidItems () {
      return this.form.fields.items.value && this.form.fields.items.value.filter(item => item.isNotValid === true).length > 0
    }
  }
}
</script>

<style lang="scss">
  .modal .modal-dialog {
    max-width: 900px;
  }
  fieldset.form-group {
    fieldset.form-group {
      margin-bottom: 0;
    }
  }
  .payout-amount {
    float:right;
    font-size: x-large;
  }
  .modal-content .currency-rate-input {
    max-width: 80px;
    display: inline;
    margin-left: 10px;
  }
  .fetch-bank-details-spinner {
    position:absolute;
    top:28px;
    right:13px;
    z-index:1;
  }
</style>
